import _ from 'lodash';
/**
 * Data transformer service.
 * Transforms data input to format acceptable to application.
 * @module dataTransformer
 */

/**
 * Transforms data to format acceptable by ui module
 * @param {array} rawData - data to be transformed
 * @return {object} - transformed data
 * @example
 * // data structure like this
 * [
 *   {
 *     'deposit_id': 620,
 *     'currency': 'RUR',
 *     'term_min': 31,
 *     'term_max': 31,
 *     'sum_min': 50000,
 *     'sum_max': 40000000,
 *     'capitalized': true,
 *     'removal': false,
 *     'refill': true,
 *     'rollover': true,
 *     'rate': 0.02,
 *     'capitalized_rate': 0.02
 *   },
 *   {
 *     'depositId': 620,
 *     'currency': 'RUR',
 *     'termMin': 91,
 *     'termMax': 91,
 *     'amountMin': 50000,
 *     'amountMax': 40000000,
 *     'capitalized': true,
 *     'removal': false,
 *     'refill': true,
 *     'rollover': true,
 *     'rate': 0.04,
 *     'capitalizedRate': 0.04
 *   },
 * ]
 * // will be transformed to
 * {
 *   'RUR': {
 *     'amount': {
 *       'min': 50000
 *       'max': 40000000
 *     },
 *     'term': {
 *       'min': 31,
 *       'max': 91,
 *     }
 *   }
 * }
 */
const transformData = ({ rawData, rawDefaultValues }) => {
    const rawDataCloned = _.map(_.cloneDeep(rawData), (item) => transformKeys(item));
    const rawDefaultValuesClone = transformKeys(rawDefaultValues);
    const currencyList = _.uniq(_.map(rawData, 'currency'));
    const isNoAmountMax = _.every(rawDataCloned, (item) => item.amountMax === null);
    const isRefillable = checkRefillable(rawDataCloned);
    const isCapitalized = checkCapitalized(rawDataCloned);
    const isTermInDays = checkTermInDays(rawDataCloned);
    let transformed = {
        data: {},
        defaultValues: rawDefaultValuesClone,
        isNoAmountMax,
        isRefillable,
        isCapitalized,
        isTermInDays,
    };

    _.each(currencyList, (name) => {
        const itemsForCurrencyName = _.filter(rawDataCloned, (item) => item.currency === name);
        const amounts = isNoAmountMax
            ? _.map(itemsForCurrencyName, 'amountMin')
            : _.union(_.map(itemsForCurrencyName, 'amountMin'), _.map(itemsForCurrencyName, 'amountMax'));
        let terms;
        let termMin;
        let termMax;

        // in case when we have a complex deposit with terms in day periods - 31-90, 91-180 etc.
        if (isTermInDays) {
            terms = _.sortBy(
                _.uniqBy(
                    _.map(itemsForCurrencyName, (item) => ({ min: item.termMin, max: item.termMax })),
                    'min'
                ),
                'min'
            );
            termMin = Math.min(..._.map(terms, 'min'));
            termMax = Math.max(..._.map(terms, 'max'));
        } else {
            terms = _.sortBy(
                _.uniqBy(
                    _.map(itemsForCurrencyName, ({ termMin }) => ({
                        termMinInDays: termMin,
                        termMinInMonths: Math.floor(termMin / 30),
                    })),
                    'termMinInDays'
                ),
                'termMinInMonths'
            );

            termMin = terms.reduce((current, next) =>
                current.termMinInMonths < next.termMinInMonths ? current : next
            );
            termMax = terms.reduce((current, next) =>
                current.termMaxInMonths > next.termMaxInMonths ? current : next
            );
        }

        transformed.data[name] = {
            amount: {
                min: Math.min(...amounts),
                max: isNoAmountMax ? 1e20 : Math.max(...amounts),
            },
            term: {
                min: termMin,
                max: termMax,
                values: terms,
            },
        };
    });
    return transformed;
};

/**
 * Transforms data keys to format acceptable by ui module
 * @param {object} rawData - data to be transformed
 * @return {object} rawData - transformed data
 */
const transformKeys = (rawData) => {
    const rawPropsToTransformedMap = {
        deposit_id: 'depositId',
        term_min: 'termMin',
        term_max: 'termMax',
        sum_min: 'amountMin',
        sum_max: 'amountMax',
        sum: 'amount',
        capitalized_rate: 'capitalizedRate',
    };
    const rawDataClone = _.cloneDeep(rawData);

    _.each(rawDataClone, (val, key) => {
        const rawPropsToTransformedMapKey = rawPropsToTransformedMap[key];

        if (rawPropsToTransformedMapKey) {
            rawDataClone[rawPropsToTransformedMapKey] = val;
            rawDataClone[key] = null;
            delete rawDataClone[key];
        }
    });

    return rawDataClone;
};

/**
 * Checks if deposit is capitalized
 * @param {object} rawData - data to be checked
 * @return {boolean} -
 */
const checkCapitalized = (rawData) => rawData.every((item) => item.capitalized);

/**
 * Checks if deposit has possibility to be refilled
 * @param {object} rawData - data to be checked
 * @return {boolean} -
 */
const checkRefillable = (rawData) => rawData.some((item) => item.refill);

/**
 * Checks if terms for deposit in days
 * @param {object} rawData - data to be checked
 * @return {boolean} -
 */
const checkTermInDays = (rawData) => rawData.every((item) => item.termMin !== item.termMax);

export { transformData, transformKeys, checkRefillable, checkTermInDays };
